import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import findIndex from 'lodash/findIndex';
import { getTargetId } from '../../utils/helpers';
import targetOptionStyles from './targetOptionStyles';
import { TargetNode } from '../../models/TargetNode';

interface TargetRow {
  _id: string;
  name: string;
  enabled?: boolean;
}

interface TargetOption {
  value: string;
  label?: string;
  enabled?: boolean;
}

interface NextSelectorProps {
  node: TargetNode;
  targets: TargetRow[];
  onChange: (option: TargetOption | null) => void;
}

const NextSelector = ({ node, targets, onChange }: NextSelectorProps) => {
  // Unlike PreviousSelector, this derives its id from the node's own c70 link
  // rather than from the target list.
  const nextTargetId = getTargetId(node.extra?.c?.c70 ?? '');

  const options = useMemo<TargetOption[]>(
    () => targets.map((target) => ({
      value: target._id,
      label: target.name,
      enabled: target.enabled,
    })),
    [targets]
  );

  // Same seeding caveat as PreviousSelector: a useState INITIALIZER, so it only
  // re-reads on mount. See SELECTED_NODE_PLAN.md.
  const [selectedOption, setSelectedOption] = useState<TargetOption | null>(() => {
    if (nextTargetId) {
      const index = findIndex(options, { value: nextTargetId });
      return {
        value: nextTargetId,
        label: index >= 0 ? options[index].label : undefined,
      };
    }
    return null;
  });

  const nextTargetName = () => {
    const index = selectedOption ? findIndex(options, { value: selectedOption.value }) : -1;
    return index >= 0 ? (options[index].label ?? '') : '';
  };

  const handleChange = (option: TargetOption | null) => {
    setSelectedOption(option);
    onChange(option); // Hack to update parent
  };

  return (
    <div>
      <Select
        value={selectedOption}
        placeholder={nextTargetName()}
        onChange={handleChange}
        options={options}
        styles={targetOptionStyles}
      />
    </div>
  );
};

export default NextSelector;
